import { Component, inject, input, OnDestroy, OnInit, output, ViewChild } from '@angular/core';

import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';

import { translate } from '@jsverse/transloco';
import { saveAs } from 'file-saver-es';

import { RealtimeConnection, RealtimeMessage } from 'app/providers/realtime.service';
import { SemantriaUsersService } from 'app/providers/semantria-api/semantria-users.service';
import { Project } from 'app/providers/storage-api/project.service';
import { StorageErrorEntry, StorageListResult } from 'app/providers/storage-api.service';
import {
  DownloadTask,
  DownloadTaskList,
  DownloadTasksService,
  TaskType,
} from 'app/providers/tasks-api/download.service';
import { AlertService } from 'app/services/alert.service';
import { DialogService } from 'app/services/dialog.service';
import { LoggerService } from 'app/services/logger.service';

import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { SystemService } from 'app/services/system.service';
import { SimpleDialogButtonColor } from '../simple-dialog.component';
import { SemantriaPreferences } from '../../providers/bridge-api/preferences.service';

@EnableClassTelemetry()
@Component({
  selector: 'app-download-center',
  templateUrl: './download-center.component.html',
  styleUrls: ['./download-center.component.scss'],
  standalone: false,
})
export class DownloadCenterComponent implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  readonly open = input<boolean>();

  readonly close = output();

  @ViewChild(MatPaginator) paginator: MatPaginator;

  displayedColumns = ['name', 'user', 'starttime', 'source', 'documents', 'filesize', 'status'];
  tableDataSource: MatTableDataSource<DownloadTask>;
  downloadTaskList: DownloadTaskList;
  taskErrorMap = new Map<string, StorageErrorEntry>();

  showStorageAvailable = false;
  storageAvailable = false;
  storageAmount = '0 GB';
  storageAmountBytes = 0;

  deleteInProgress = false;
  downloadInProgress = false;

  usersById = new Map<string, string>();
  allProjects: StorageListResult<Project> = {};

  isLoading = false;
  totalRows = 0;
  pageSize = 5;
  currentPage = 0;
  pageSizeOptions: number[] = [5, 10, 25, 100];
  sortField = 'createdAt';
  sortOrder = 'DESC';
  realtimeHandler: RealtimeConnection;

  private readonly _excludedTaskTypes: TaskType[] = [
    TaskType.COPY_COLLECTION,
    TaskType.DELETE_PROJECT,
    TaskType.DELETE_COLLECTION,
    TaskType.DO_NOTHING,
    TaskType.GENERATE_EMBEDDINGS,
    TaskType.AI_INSIGHT_PREP_TASK,
    TaskType.AI_INSIGHT_COMPLETION_TASK,
  ];
  public readonly preferencesService = inject(SemantriaPreferences);

  constructor(
    private dialogService: DialogService,
    private alertService: AlertService,
    private downloadTaskService: DownloadTasksService,
    private userService: SemantriaUsersService,
    private systemService: SystemService,
    private logger: LoggerService
  ) {
    this.tableDataSource = new MatTableDataSource([]);
  }

  async ngOnInit() {
    const users = await this.userService.getUsers();
    users.forEach((user) => {
      let userName = ((user.first_name || '') + ' ' + (user.last_name || '')).trim();
      if (!userName) {
        userName = user.email;
      }
      this.usersById.set(user.user_id, userName);
    });

    this.realtimeHandler = this.downloadTaskService.on(
      'tasks',
      this.handleDownloadTaskEvent.bind(this)
    );

    this.getTasks();
  }

  ngOnDestroy() {
    if (this.realtimeHandler) {
      this.realtimeHandler.disconnect();
    }
  }

  private handleDownloadTaskEvent(data: RealtimeMessage<DownloadTask>) {
    const task: DownloadTask = data.object;
    if (data.verb === 'UPDATE') {
      const existingTaskIdx = this.tableDataSource.data.findIndex((d) => d.id === task.id);
      if (existingTaskIdx > -1) {
        this.parseTaskError(task);
        this.tableDataSource.data[existingTaskIdx] = task;
        this.tableDataSource.data = this.tableDataSource.data;
        if (task.taskResult?.fileSize) {
          this.decrementStorageAmountAvailable(task.taskResult.fileSize);
        }
      }
    } else if (data.verb === 'CREATE') {
      // Note: this works because currently we only sort by createdAt, DESC
      if (!this._excludedTaskTypes.includes(TaskType[task.type])) {
        this.tableDataSource.data.unshift(task);
        this.tableDataSource.data = this.tableDataSource.data;
      }
    } else if (data.verb === 'DELETE') {
      this.tableDataSource.data = this.tableDataSource.data.filter((d) => d.id !== data.object.id);
    }
  }

  cancel() {
    this.close.emit();
  }

  getTasks(): void {
    this.isLoading = true;
    const params = {
      page: this.currentPage,
      pageSize: this.pageSize,
      sortField: this.sortField,
      sortOrder: this.sortOrder,
      type: this.getIncludedTaskTypes(),
    };

    this.downloadTaskService
      .getDownloadTasks(params)
      .then((result) => {
        this.downloadTaskList = result;
        this.downloadTaskList.items.forEach((task) => this.parseTaskError(task));
        this.showTasks();
      })
      .catch((error) => {
        this.isLoading = false;
        this.alertService.showErrorToast(translate('download-center.failed-to-get'), error);
        this.logger.error('Failed to get download tasks', { error });
      });
  }

  private getIncludedTaskTypes(): string {
    const allTaskTypes = Object.keys(TaskType).filter((k) => isNaN(Number(k)));

    const includedTaskTypes = allTaskTypes.filter(
      (k) => !this._excludedTaskTypes.includes(TaskType[k])
    );

    return includedTaskTypes.join(',');
  }

  private setStorageAmountAvailable(bytes: number) {
    this.storageAvailable = bytes > 0;
    this.storageAmountBytes = bytes;
    this.storageAmount = this.formatBytes(this.storageAmountBytes);
  }

  private incrementStorageAmountAvailable(bytes: number) {
    this.setStorageAmountAvailable(this.storageAmountBytes + bytes);
  }

  private decrementStorageAmountAvailable(bytes: number) {
    this.incrementStorageAmountAvailable(-1 * bytes);
  }

  private parseTaskError(task: DownloadTask) {
    if (task.status === 'ERROR' && task.taskResult?.message) {
      // error message can either be an object where key is an HTTP code, value is an error object
      // or just a simple string
      try {
        const taskError = task.taskResult.message.split(' : ')[1].slice(1, -1);
        this.taskErrorMap.set(task.id, JSON.parse(taskError));
      } catch (err) {
        this.taskErrorMap.set(task.id, {
          reference_id: undefined,
          error_code: undefined,
          error: task.taskResult.message,
        });
      }
    }
  }

  getErrorForTask(task: DownloadTask) {
    if (!this.taskErrorMap.has(task.id)) {
      return null;
    }

    return this.taskErrorMap.get(task.id).error;
  }

  copyTaskError(task: DownloadTask) {
    this.systemService.copyToClipboard(JSON.stringify(this.taskErrorMap.get(task.id)));

    this.alertService.showToast(translate('download-center.error-copied'));
  }

  showTasks() {
    this.setStorageAmountAvailable(this.downloadTaskList.storageBytesRemaining);
    this.showStorageAvailable = true;

    this.tableDataSource.data = this.downloadTaskList.items;
    setTimeout(() => {
      this.paginator.pageIndex = this.currentPage;
      this.paginator.length = this.downloadTaskList.count;
    });
    this.isLoading = false;
  }

  pageChanged(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.currentPage = event.pageIndex;
    this.getTasks();
  }

  deleteTask(downloadTask: DownloadTask): void {
    if (!this.deleteInProgress) {
      this.dialogService
        .showSimpleDialog({
          title: translate('download-center.delete-file'),
          content: translate('download-center.deleting-file-non-recoverable', {
            fileName: downloadTask.taskPayload.filename,
          }),
          confirmTitle: translate('download-center.delete-report'),
          confirmColor: SimpleDialogButtonColor.red,
        })
        .afterClosed()
        .subscribe(async (result) => {
          if (result) {
            this.alertService.showToast(
              translate('download-center.deleting-file', {
                fileName: downloadTask.taskPayload.filename,
              })
            );
            try {
              this.deleteInProgress = true;
              this.downloadTaskService
                .deleteDownloadTask(downloadTask.id)
                .then(() => {
                  this.deleteInProgress = false;
                  this.getTasks();
                })
                .catch((error) => {
                  this.alertService.showErrorToast(
                    translate('download-center.failed-to-delete'),
                    error
                  );
                  this.deleteInProgress = false;
                });
            } catch (error) {
              this.alertService.showErrorToast(
                translate('download-center.failed-to-delete-file'),
                error
              );
              return;
            }
          }
        });
    }
  }

  downloadDocument(downloadTask: DownloadTask): void {
    if (!this.downloadInProgress) {
      this.downloadInProgress = true;
      this.downloadTaskService
        .downloadDocument(downloadTask.taskResult.exportId)
        .then((blob) => {
          saveAs(blob, downloadTask.taskPayload.filename.replace(/\./g, '_'));
        })
        .catch((error) => {
          this.logger.error('Failed to download document', { error });
        })
        .finally(() => (this.downloadInProgress = false));
    }
  }

  formatBytes(bytes, decimals = 2): string {
    if (!+bytes) {
      return '-';
    }

    const k = 1000;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }
}
